import type { GridCell, Season, Weather, WeatherState } from '../types';

export const WEATHER_DISPLAY_NAMES: Readonly<Record<Weather, string>> = Object.freeze({
  sunny: '晴れ',
  cloudy: '曇り',
  rainy: '雨',
  snowy: '雪',
  windy: '強風',
  petalFlurry: '花吹雪',
  dappledSunlight: '木漏れ日',
});

export function getWeatherDisplayName(weather: Weather): string {
  return WEATHER_DISPLAY_NAMES[weather];
}

interface WeatherRule {
  weight: number;
  duration: readonly [number, number];
  wind: readonly [number, number];
  intensity: readonly [number, number];
  temperatureModifier: number;
}

type WeatherTable = Record<Weather, WeatherRule>;

const rule = (weight: number, duration: readonly [number, number], wind: readonly [number, number], intensity: readonly [number, number], temperatureModifier: number): WeatherRule => ({ weight, duration, wind, intensity, temperatureModifier });

const TABLES: Record<Season, WeatherTable> = {
  spring: {
    sunny: rule(25, [360, 840], [0.08, 0.28], [0.55, 0.9], 2),
    cloudy: rule(18, [300, 720], [0.1, 0.32], [0.4, 0.75], -1),
    rainy: rule(20, [180, 540], [0.18, 0.48], [0.45, 0.9], -3),
    snowy: rule(0, [120, 300], [0.1, 0.35], [0.2, 0.5], -7),
    windy: rule(13, [120, 360], [0.55, 0.9], [0.5, 0.9], -1),
    petalFlurry: rule(14, [90, 240], [0.35, 0.7], [0.6, 1], 1),
    dappledSunlight: rule(10, [180, 480], [0.08, 0.25], [0.5, 0.85], 2),
  },
  summer: {
    sunny: rule(38, [420, 960], [0.05, 0.25], [0.7, 1], 5),
    cloudy: rule(14, [240, 600], [0.08, 0.3], [0.4, 0.75], 1),
    rainy: rule(20, [120, 420], [0.25, 0.65], [0.6, 1], -4),
    snowy: rule(0, [120, 240], [0.1, 0.3], [0.2, 0.4], -12),
    windy: rule(12, [120, 360], [0.55, 1], [0.55, 1], 0),
    petalFlurry: rule(0, [90, 180], [0.4, 0.7], [0.5, 0.8], 0),
    dappledSunlight: rule(16, [180, 540], [0.05, 0.22], [0.55, 0.9], 3),
  },
  autumn: {
    sunny: rule(24, [300, 720], [0.1, 0.32], [0.5, 0.85], 1),
    cloudy: rule(24, [300, 780], [0.12, 0.38], [0.45, 0.8], -2),
    rainy: rule(18, [180, 480], [0.2, 0.55], [0.45, 0.85], -4),
    snowy: rule(2, [120, 300], [0.15, 0.4], [0.25, 0.55], -7),
    windy: rule(24, [180, 480], [0.58, 1], [0.55, 1], -2),
    petalFlurry: rule(0, [90, 180], [0.4, 0.7], [0.5, 0.8], 0),
    dappledSunlight: rule(8, [180, 420], [0.08, 0.28], [0.45, 0.8], 1),
  },
  winter: {
    sunny: rule(18, [240, 600], [0.08, 0.3], [0.4, 0.7], -2),
    cloudy: rule(27, [360, 840], [0.12, 0.38], [0.4, 0.75], -5),
    rainy: rule(8, [120, 360], [0.18, 0.5], [0.35, 0.7], -4),
    snowy: rule(32, [180, 600], [0.12, 0.5], [0.4, 1], -9),
    windy: rule(13, [120, 420], [0.6, 1], [0.55, 1], -7),
    petalFlurry: rule(0, [90, 180], [0.4, 0.7], [0.5, 0.8], 0),
    dappledSunlight: rule(2, [120, 300], [0.05, 0.2], [0.35, 0.65], -1),
  },
};

export class WeatherSystem {
  private state: WeatherState;
  private readonly listeners = new Set<(state: Readonly<WeatherState>) => void>();

  constructor(initialState?: Partial<WeatherState>, private readonly random: () => number = Math.random) {
    this.state = { current: 'sunny', next: 'cloudy', windStrength: 0.15, intensity: 0.7, temperatureModifier: 2, changesAtGameTick: 480, ...initialState };
  }

  get snapshot(): Readonly<WeatherState> { return Object.freeze({ ...this.state }); }
  subscribe(listener: (state: Readonly<WeatherState>) => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  evaluate(totalTicks: number, season: Season): boolean { if (totalTicks < this.state.changesAtGameTick) return false; this.transitionTo(this.state.next, totalTicks, season); return true; }
  force(weather: Weather, totalTicks: number, season: Season): void { this.transitionTo(weather, totalTicks, season); }

  applySoilMoisture(cells: readonly GridCell[], elapsedGameMinutes: number, evaporationMultiplier = 1): GridCell[] {
    const minutes = Math.max(0, elapsedGameMinutes);
    return cells.map((cell) => {
      if (cell.terrain !== 'soil' && cell.terrain !== 'grass') return { ...cell };
      let delta = -0.0018 * minutes * evaporationMultiplier;
      if (this.state.current === 'sunny') delta *= 1.65;
      if (this.state.current === 'cloudy') delta *= 0.7;
      if (this.state.current === 'dappledSunlight') delta *= 0.85;
      if (this.state.current === 'windy') delta *= 1.35;
      if (this.state.current === 'rainy') delta = 0.018 * minutes * this.state.intensity;
      if (this.state.current === 'snowy') delta = 0.0025 * minutes * this.state.intensity;
      return { ...cell, soilMoisture: clamp(cell.soilMoisture + delta, 0, 100), snowDepth: this.state.current === 'snowy' ? clamp(cell.snowDepth + 0.004 * minutes * this.state.intensity, 0, 100) : clamp(cell.snowDepth - 0.001 * minutes, 0, 100) };
    });
  }

  getGrowthMultiplier(): number {
    if (this.state.current === 'sunny') return 1.1;
    if (this.state.current === 'dappledSunlight') return 1.08;
    if (this.state.current === 'rainy') return 1.04;
    if (this.state.current === 'snowy') return 0;
    return 1;
  }

  getPestMultiplier(): number { return this.state.current === 'windy' ? 0.85 : 1; }

  private transitionTo(weather: Weather, totalTicks: number, season: Season): void {
    const currentRule = TABLES[season][weather];
    this.state = { current: weather, next: this.pickWeather(season, weather), windStrength: randomBetween(currentRule.wind, this.random), intensity: randomBetween(currentRule.intensity, this.random), temperatureModifier: currentRule.temperatureModifier, changesAtGameTick: totalTicks + Math.round(randomBetween(currentRule.duration, this.random)) };
    for (const listener of this.listeners) listener(this.snapshot);
  }

  private pickWeather(season: Season, exclude?: Weather): Weather {
    const entries = (Object.entries(TABLES[season]) as Array<[Weather, WeatherRule]>).filter(([weather, value]) => value.weight > 0 && weather !== exclude);
    let roll = this.random() * entries.reduce((sum, [, value]) => sum + value.weight, 0);
    for (const [weather, value] of entries) { roll -= value.weight; if (roll <= 0) return weather; }
    return entries.at(-1)?.[0] ?? 'sunny';
  }
}

function randomBetween(range: readonly [number, number], random: () => number): number { return range[0] + (range[1] - range[0]) * random(); }
function clamp(value: number, minimum: number, maximum: number): number { return Math.min(maximum, Math.max(minimum, value)); }
